import { FunctionComponent, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Divider, Flex, Modal, Spin, Table, TableProps, Tooltip } from 'antd';

// services
import { onDeleteShareholder, onGetShareholders } from '../services/shareholdersService';
import NotificationService from 'core/services/notification';
import StatusComponent from 'components/status/status';
import NoData from 'components/noData/noData';
import Icon from 'utils/icon';
import ShareholderModal from './shareholderModal';
import ListButton from 'components/listButton/listButton';
import { Shareholder } from 'core/models/shareholder';
import TableTotal from 'components/tableTotal/tableTotal';

export interface ShareholderListComponentRef {
	refreshShareholders: () => void;
}

interface ShareholderListComponentProps {
	remainingShares: number;
	editable: boolean;
	onChange: () => void;
}

const ShareholderListComponent = forwardRef<ShareholderListComponentRef, ShareholderListComponentProps>(
	({ onChange, remainingShares, editable }, ref) => {
		const { t } = useTranslation();

		const [isShareholdersLoading, setIsShareholdersLoading] = useState(true);
		const [isDeletingShareholder, setIsDeletingShareholder] = useState(false);
		const [modalOpen, setModalOpen] = useState(false);
		const [updatedShareholder, setUpdatedShareholder] = useState<Shareholder | null>(null);
		const [shareholders, setShareholders] = useState<Shareholder[]>([]);

		//pagination
		const [currentPage, setCurrentPage] = useState<number>(1);
		const [totalRecords, setTotalRecords] = useState<number>(0);

		const columns: TableProps<Shareholder>['columns'] = [
			{
				title: t('COMMON.SHAREHOLDER'),
				dataIndex: 'name',
				key: 'name',
			},
			{
				title: t('COMMON.PHONE_NUMBER'),
				dataIndex: 'full_phone_number',
				key: 'phone_number',
				render: (value) => <div style={{ direction: 'ltr' }}>{value}</div>,
			},
			{
				title: t('COMMON.NATIONAL_ID'),
				dataIndex: 'national_id',
				key: 'national_id',
			},
			{
				title: t('SHAREHOLDERS_REGISTRY.OWNED_SHARES'),
				dataIndex: 'free_common_shares_count',
				key: 'free_common_shares_count',
				render: (value) => Number(value).toLocaleString(),
			},
			{
				title: t('COMMON.FOUNDER'),
				dataIndex: 'founder',
				key: 'founder',
				render: (value) => <StatusComponent text={t(`COMMON.${value.toUpperCase()}`)} color={value == 'Yes' ? 'green' : 'red'} />,
			},
			{
				title: t('COMMON.EXECUTIVE'),
				dataIndex: 'executive',
				key: 'executive',
				render: (value) => <StatusComponent text={t(`COMMON.${value.toUpperCase()}`)} color={value == 'Yes' ? 'green' : 'red'} />,
			},
			{
				title: t('COMMON.FULLY_PAID'),
				dataIndex: 'fully_paid',
				key: 'fully_paid',
				render: (value) => <StatusComponent text={t(`COMMON.${value ? 'YES' : 'NO'}`)} color={value ? 'green' : 'red'} />,
			},
			{
				title: t('COMMON.ACTIONS'),
				dataIndex: 'actions',
				key: 'actions',
				render: (_, shareholder) =>
					editable && (
						<Flex gap={10} justify="center">
							<ListButton type="edit" text={t('COMMON.EDIT')} onClick={() => updateShareholderHandler(shareholder as Shareholder)} />
							<ListButton type="delete" text={t('COMMON.DELETE')} onClick={() => deleteShareholderConfirm(shareholder as Shareholder)} />
						</Flex>
					),
			},
		];

		useEffect(() => {
			getShareholders();
		}, []);

		const getShareholders = async () => {
			try {
				setIsShareholdersLoading(true);
				const response = await onGetShareholders();
				setShareholders(response.shareholders);
				setTotalRecords(response.pagination.total);

				setIsShareholdersLoading(false);
				onChange();
			} catch (error) {
				setIsShareholdersLoading(false);
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			}
		};

		const fetchMoreData = async (page: number) => {
			try {
				setIsShareholdersLoading(true);

				const response = await onGetShareholders(page);
				setShareholders(response.shareholders);

				setCurrentPage(page);
				setIsShareholdersLoading(false);
			} catch (error) {
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
				setIsShareholdersLoading(false);
			}
		};

		const addShareholderHandler = async () => {
			setModalOpen(false);
			getShareholders();
		};

		const cancelShareholderHandler = () => {
			setUpdatedShareholder(null);
			setModalOpen(false);
		};

		const updateShareholderHandler = async (shareholder: Shareholder) => {
			setUpdatedShareholder(shareholder);
			setModalOpen(true);
		};

		const deleteShareholderHandler = async (shareholderId: number) => {
			try {
				setIsDeletingShareholder(true);
				await onDeleteShareholder(shareholderId);
				setIsDeletingShareholder(false);
				NotificationService.success(t('COMMON.SUCCESS'), t('SHAREHOLDERS_REGISTRY.SHAREHOLDER_DELETED'));
				getShareholders();
			} catch (error) {
				setIsDeletingShareholder(false);
				NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
			}
		};

		const deleteShareholderConfirm = (shareholder: Shareholder) => {
			Modal.confirm({
				title: t('COMMON.ATTENTION'),
				content: (
					<div className="my-2">
						<p className="mb-0">{t('SHAREHOLDERS_REGISTRY.DELETE_SHAREHOLDER_CONFIRM')}</p>
						<p className="mb-0 fw-medium">{shareholder.name}</p>
					</div>
				),
				okText: t('COMMON.CONFIRM'),
				cancelText: t('COMMON.CANCEL'),
				okButtonProps: {
					danger: true,
					loading: isDeletingShareholder,
				},
				onOk: () => deleteShareholderHandler(shareholder.id),
			});
		};

		useImperativeHandle(ref, () => ({
			refreshShareholders: getShareholders,
		}));

		return (
			<>
				<Flex justify="space-between" align="center" className="mb-3">
					<h6
						style={{
							fontSize: '1.1rem',
							color: 'var(--primary)',
							marginBottom: 0,
						}}>
						{t('SHAREHOLDERS_REGISTRY.TOTAL_SHAREHOLDERS')}:{' '}
						{isShareholdersLoading ? <Spin size="small" style={{ marginInlineStart: 5 }} /> : totalRecords}
					</h6>

					{editable && (
						<Tooltip color="red" title={remainingShares == 0 ? t('SHAREHOLDERS_REGISTRY.ALL_SHARES_ALLOCATED_TOOLTIP') : ''}>
							<Button size="middle" type="primary" onClick={() => setModalOpen(true)} disabled={remainingShares == 0}>
								<Icon name="plus" color={remainingShares == 0 ? '#ccc' : '#fff'} width={15} height={15} />
								{t('SHAREHOLDERS_REGISTRY.ADD_NEW_SHAREHOLDER')}
							</Button>
						</Tooltip>
					)}
				</Flex>

				<Divider />

				<Table
					columns={columns}
					dataSource={shareholders}
					scroll={{ x: 600 }}
					rowKey="national_id"
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
					}}
					locale={{
						emptyText: <NoData message={t('SHAREHOLDERS_REGISTRY.NO_DATA_ENTERED')} />,
					}}
					loading={isShareholdersLoading}
				/>

				<ShareholderModal
					shareholderData={updatedShareholder!}
					open={modalOpen}
					onCancel={cancelShareholderHandler}
					onConfirm={addShareholderHandler}
					remainingShares={remainingShares}
				/>
			</>
		);
	},
);

export default ShareholderListComponent;
